import { computed, inject, Injectable } from '@angular/core';
import { BaseWorkflowStateService } from '../../base-workflow-state.service';
import { InternetFlowStateService } from '../internet-flow-state.service';
import { InternetAdditionalOfferData } from '../../../../../../../models/order/workflow/internet/internet-additional-offer.model';
import { AdditionalOfferData, OfferType } from '../../../../../../../models/order/workflow/additional-offer.model';
import { InternetProductResponseService } from '../../../order-api-response/internet-pd-response.service';
import { Choice } from '../../../../../../../models/order/api-models/order-pd-response.model';
import { isInternetPromotionChoiceCode } from '../../../../../../../constants/app-constants';



@Injectable({ providedIn: 'root' })
export class AdditionalInternetOffersStateService extends BaseWorkflowStateService<InternetAdditionalOfferData> {
    private flowStateService = inject(InternetFlowStateService);
    private internetPdResponseService = inject(InternetProductResponseService);
    override stateKey: string = 'internetOffers';

    customizations() {
        this.internetPdResponseService.setupCustomizations();
        return this.internetPdResponseService.getCustomizations();
    }

    offers = computed(() => {
        return this.state()?.offers ?? [];
    });

    includedOffers = computed(() => {
        return this.state().offers?.filter(offer => offer.type === OfferType.Required) || [];
    });

    additionalOffers = computed(() => {
        return this.state().offers?.filter(offer => offer.type === OfferType.Additional) || [];
    });

    equipmentOffers = computed(() => {
        return this.state().offers?.filter(offer => offer.type === OfferType.Equipment) || [];
    });

    findOfferByCustomizationAndChoice(customizationCode: string, choiceCode: string): AdditionalOfferData | undefined {
        return this.offers().find(offer => offer.customizationCode === customizationCode && (offer.choice?.ChoiceCode === choiceCode || offer.choiceCode === choiceCode));
    }

    findOffersByCustomization(customizationCode: string): AdditionalOfferData[] {
        return this.offers().filter(offer => offer.customizationCode === customizationCode);
    }

    findDropdownSelectedOffer(customizationCode: string): AdditionalOfferData | undefined {
        return this.offers().find(offer => offer.customizationCode === customizationCode);
    }

    getExcludedIncludedChoiceCodes(selectedChoiceCodes: string[]): Set<string> {
        const parseCodes = (value?: string | null) => (value || '').split(',').map(code => code.trim()).filter(Boolean);
        const excluded = new Set<string>();
        const included = new Set<string>();
        const choices = this.internetPdResponseService.getCustomizations()?.flatMap(cust => cust.customization.Choices?.Choice ?? []) ?? [];
        for (const choice of choices) {
            const isSelected = selectedChoiceCodes.includes(choice.ChoiceCode);
            parseCodes(choice.IncludedOfferCodes).forEach(code => (isSelected ? included : excluded).add(code));
            if (isSelected) {
                parseCodes(choice.IncludedOfferCodesHide).forEach(code => excluded.add(code));
            }
        }
        included.forEach(code => excluded.delete(code));
        return excluded;
    }

    getAllIncludedWithPackageChoices(selectedChoiceCodes: string[] = []): AdditionalOfferData[] {
        const choices = this.customizations()?.filter(x => x.customization.Choices.Choice.some((choice: Choice) => choice.IncludedWithPackage && (choice.IncludedWithPackage === true || choice.IncludedWithPackage.toLocaleLowerCase() === 'true')))?.flatMap(cust => cust.customization.Choices.Choice.filter((choice: Choice) => choice.IncludedWithPackage && (choice.IncludedWithPackage === true || choice.IncludedWithPackage.toLocaleLowerCase() === 'true'))) || [];
        const excludedChoiceCodes = this.getExcludedIncludedChoiceCodes(selectedChoiceCodes);
        const pdDefaultOffers: AdditionalOfferData[] = []
        for (const offer of choices) {
            if (excludedChoiceCodes.has(offer.ChoiceCode)) continue;
            const customization = this.customizations().find(cust => cust.customization.Choices.Choice.some(choice => choice.ChoiceCode === offer.ChoiceCode));
            const type = customization?.customization.SubGroupName === 'IncludedOffers' ? OfferType.Required : OfferType.Additional;
            pdDefaultOffers.push({ customizationCode: customization?.customization.CustomizationCode, choiceCode: offer.ChoiceCode, type, usoc: offer.Usoc });
        }
        return pdDefaultOffers;
    }

    replaceIncludedPromotions(): void {
        const currentOffers = this.offers();
        const promotions = this.getAllIncludedWithPackageChoices(currentOffers.map(offer => offer.choiceCode))
            .filter(offer => isInternetPromotionChoiceCode(offer.choiceCode));
        this.updateData({
            offers: [...currentOffers.filter(offer => !isInternetPromotionChoiceCode(offer.choiceCode)), ...promotions]
        });
    }

    addOffer(offer: AdditionalOfferData): void {
        const currentState = this.state();
        const existingOfferIndex = currentState?.offers?.findIndex(o => o.choiceCode === offer.choiceCode);
        if (existingOfferIndex > -1) {
            currentState.offers[existingOfferIndex] = offer;
        } else {
            currentState.offers.push(offer);
        }
        this.updateData(currentState);
    }

    closingOffers = computed(() => this.offers().filter(offer => offer.type === OfferType.Closing));

    setClosingOffer(selection: { customizationCode: string; choiceCode?: string; accepted: boolean; usoc?: string }, singleSelect: boolean): void {
        const remaining = this.offers().filter(offer => !(offer.type === OfferType.Closing &&
            (singleSelect ? offer.customizationCode === selection.customizationCode : offer.choiceCode === selection.choiceCode)));
        const offers = selection.accepted && selection.choiceCode
            ? [...remaining, { choiceCode: selection.choiceCode, customizationCode: selection.customizationCode, type: OfferType.Closing, usoc: selection.usoc }]
            : remaining;
        this.updateData({ offers });
    }

    removeOffer(choiceCode: string): void {
        const currentState = this.state();
        currentState.offers = currentState.offers?.filter(o => o.choiceCode !== choiceCode);
        this.updateData(currentState);
    }

addABPoffer(abpEnabled: boolean): void {
    const abpOffer = this.internetPdResponseService.getInternetAbpDecision(abpEnabled);
    if (!abpOffer) {
        return;
    }
    this.addOffer({
        choiceCode: abpOffer.choiceCode,
        customizationCode: abpOffer.customizationCode,
        type: OfferType.Required,
        usoc: abpOffer.choiceCode
    });
    const oppositeOffer = this.internetPdResponseService.getInternetAbpDecision(!abpEnabled);
    if (!oppositeOffer) {
        return;
    }
    this.removeOffer(oppositeOffer.choiceCode);
}

    protected override saveLocalDraft(): void {
        this.flowStateService.updateData({ [this.stateKey]: this.state() });
    }

    resetData() {
        this.state.set(null);
        this.saveLocalDraft();
    }
}
